import React, { Component, useCallback, useContext, useEffect, useRef, useState } from "react";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import TextField from "@material-ui/core/TextField";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import TouchAppIcon from "@material-ui/icons/TouchApp";
import { DropTarget } from "react-dnd";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../../context/auth-context";
import RulesContext from "../../../../context/rules-context";
import MarkdownRenderer from "../MarkdownRenderer";
import QiaAvatar from "../QiaAvatar";
import QiaLoader from "../QiaLoader";
import "../ruleAgentTheme.css";
import { translateOutcomeTreeData } from "../treeUtils";
import { buildCompanyEventPayload, openRuleAgentSession, streamRuleAgentTurn } from "../ruleAgentClient";
import { chatAttachmentDndType } from "../EngineUtilsComponents/SelectionDragChip";
import MiniRuleTree from "./MiniRuleTree";
import RuleArtifactCard from "./RuleArtifactCard";
import RuleChatWelcome from "./RuleChatWelcome";

const MIN_PANEL_WIDTH = 300;
const MAX_PANEL_WIDTH = 700;
const DEFAULT_PANEL_WIDTH = 380;

function buildCurrentRuleJson(treeData, userVariablesAvailable, ruleName) {
    let ruleContext = { tree_data: translateOutcomeTreeData(treeData) };
    if (userVariablesAvailable && userVariablesAvailable.length > 0) {
        ruleContext.user_variables = userVariablesAvailable;
    }
    if (ruleName) ruleContext.name = ruleName;
    return JSON.stringify(ruleContext);
}

const chatDropSpec = {
    drop: (componentProps, monitor) => {
        let item = monitor.getItem();
        if (item && item.attachments) componentProps.onDropAttachments(item.attachments);
    },
};
const chatDropCollect = (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
    canDrop: monitor.canDrop(),
});

class chatDropAreaBase extends Component {
    render() {
        const { connectDropTarget, isOver, canDrop, children, style } = this.props;
        return connectDropTarget(
            <div style={style} className={canDrop ? (isOver ? "qia-drop-zone qia-drop-active" : "qia-drop-zone qia-drop-ready") : "qia-drop-zone"}>
                {children}
            </div>
        );
    }
}

// Accepts selection chips dragged from the rule tree — both over the open
// panel and over the collapsed launcher.
const ChatDropArea = DropTarget(chatAttachmentDndType, chatDropSpec, chatDropCollect)(chatDropAreaBase);

function ChatAttachmentCard({ attachment, onRemove, t }) {
    return (
        <div className="qia-attachment-card">
            <div className="qia-attachment-header">
                <span className="qia-attachment-label">
                    {attachment.label || t("Trecho da regra")}
                </span>
                {onRemove && (
                    <button
                        type="button"
                        className="qia-attachment-remove"
                        aria-label={t("Remover trecho")}
                        onClick={onRemove}
                    >
                        <CloseIcon style={{ width: "13px", height: "13px" }} />
                    </button>
                )}
            </div>
            <MiniRuleTree treeData={attachment.tree_data} />
        </div>
    );
}

function RuleChatMessage({ message, t, rulesContext, userVariablesAvailable, onInsertGeneratedNodes }) {
    if (message.role === "user") {
        return (
            <div className="qia-msg-user qia-fadeup">
                <div className="qia-user-bubble">
                    {(message.attachments || []).map((attachment, index) => (
                        <ChatAttachmentCard key={index} attachment={attachment} t={t} />
                    ))}
                    {message.text}
                </div>
            </div>
        );
    }

    if (message.status === "error" && !message.text) return null;

    if (message.status === "loading") {
        return (
            <div className="qia-msg-bot">
                <QiaLoader label={t("Assistente está digitando...")} size={40} />
            </div>
        );
    }

    return (
        <div className="qia-msg-bot qia-fadeup">
            <QiaAvatar size={26} />
            <div className="qia-bot-bubble">
                <MarkdownRenderer source={message.text} />
                {(message.rules || []).map((artifact, index) => (
                    <RuleArtifactCard
                        key={index}
                        artifact={artifact}
                        rulesContext={rulesContext}
                        userVariablesAvailable={userVariablesAvailable}
                        onInsertNodes={onInsertGeneratedNodes}
                    />
                ))}
            </div>
        </div>
    );
}

function RuleChat(props) {
    const { treeData, userVariablesAvailable, ruleName, onInsertGeneratedNodes, selectionMode, onSelectionModeToggle } = props;
    const { t } = useTranslation();
    const { keycloak_adapter } = useContext(AuthContext);
    const rulesContext = useContext(RulesContext);

    const [isOpen, setIsOpen] = useState(false);
    const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [hasChatError, setHasChatError] = useState(false);

    const sessionIdRef = useRef(null);
    const lastSentRuleJsonRef = useRef(null);
    const companyEventSentRef = useRef(false);
    const isSendingRef = useRef(false);
    const abortControllerRef = useRef(null);
    const fullAssistantTextRef = useRef("");
    const assistantRulesRef = useRef([]);
    const messageListRef = useRef(null);
    const resizeStateRef = useRef({ resizing: false, startX: 0, startWidth: DEFAULT_PANEL_WIDTH });

    useEffect(() => {
        if (messageListRef.current) {
            messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        return () => {
            abortControllerRef.current && abortControllerRef.current.abort();
        };
    }, []);

    const handleResizeMouseMove = useCallback((event) => {
        if (!resizeStateRef.current.resizing) return;
        let delta = event.clientX - resizeStateRef.current.startX;
        let newWidth = resizeStateRef.current.startWidth - delta;
        newWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, newWidth));
        setPanelWidth(newWidth);
    }, []);

    const handleResizeMouseUp = useCallback(() => {
        resizeStateRef.current.resizing = false;
        document.removeEventListener("mousemove", handleResizeMouseMove);
        document.removeEventListener("mouseup", handleResizeMouseUp);
    }, [handleResizeMouseMove]);

    useEffect(() => {
        return () => {
            document.removeEventListener("mousemove", handleResizeMouseMove);
            document.removeEventListener("mouseup", handleResizeMouseUp);
        };
    }, [handleResizeMouseMove, handleResizeMouseUp]);

    const handleResizeMouseDown = (event) => {
        resizeStateRef.current = { resizing: true, startX: event.clientX, startWidth: panelWidth };
        document.addEventListener("mousemove", handleResizeMouseMove);
        document.addEventListener("mouseup", handleResizeMouseUp);
    };

    const handleDropAttachments = (attachments) => {
        setPendingAttachments((previous) => [...previous, ...attachments]);
        setIsOpen(true);
    };

    const removePendingAttachment = (indexToRemove) => {
        setPendingAttachments((previous) =>
            previous.filter((_, index) => index !== indexToRemove)
        );
    };

    const updateAssistantMessage = (assistantMessageId, updates) => {
        setMessages((previousMessages) =>
            previousMessages.map((message) =>
                message.id === assistantMessageId ? { ...message, ...updates } : message
            )
        );
    };

    const sendText = (rawText) => {
        let userText = (rawText || "").trim();
        let attachments = pendingAttachments;
        if (isSendingRef.current) return;
        if (!userText && attachments.length === 0) return;
        if (!userText) {
            userText = t("O que estes trechos fazem nesta regra?");
        }

        let token = keycloak_adapter && keycloak_adapter.token;
        if (!token) {
            setHasChatError(true);
            return;
        }

        setInputValue("");
        setPendingAttachments([]);
        setHasChatError(false);

        let currentRuleJson = buildCurrentRuleJson(treeData, userVariablesAvailable, ruleName);
        let ruleContext = null;
        if (lastSentRuleJsonRef.current !== currentRuleJson) {
            ruleContext = JSON.parse(currentRuleJson);
        }
        let companyEvent = companyEventSentRef.current ? null : buildCompanyEventPayload(rulesContext);

        let userMessageId = "u-" + Date.now() + "-" + Math.random();
        let assistantMessageId = "a-" + Date.now() + "-" + Math.random();

        setMessages((previousMessages) => [
            ...previousMessages,
            { id: userMessageId, role: "user", text: userText, attachments: attachments },
            { id: assistantMessageId, role: "assistant", text: "", rules: [], status: "loading" }
        ]);

        setIsSending(true);
        isSendingRef.current = true;
        fullAssistantTextRef.current = "";
        assistantRulesRef.current = [];

        let abortController = new AbortController();
        abortControllerRef.current = abortController;

        let runTurn = (sessionId) => {
            if (abortController.signal.aborted) return;
            return streamRuleAgentTurn({
                sessionId,
                token,
                prompt: userText,
                ruleContext,
                companyEvent,
                attachments,
                signal: abortController.signal,
                onDelta: (data) => {
                    if (abortController.signal.aborted) return;
                    fullAssistantTextRef.current += data.text || "";
                    updateAssistantMessage(assistantMessageId, {
                        text: fullAssistantTextRef.current,
                        status: "streaming"
                    });
                },
                onRule: (data) => {
                    if (abortController.signal.aborted) return;
                    if (!data || !data.rule) return;
                    assistantRulesRef.current = [...assistantRulesRef.current, data];
                    updateAssistantMessage(assistantMessageId, {
                        rules: assistantRulesRef.current,
                        status: "streaming"
                    });
                },
                onDone: () => {
                    if (abortController.signal.aborted) return;
                    lastSentRuleJsonRef.current = currentRuleJson;
                    if (companyEvent) companyEventSentRef.current = true;
                    updateAssistantMessage(assistantMessageId, {
                        text: fullAssistantTextRef.current,
                        rules: assistantRulesRef.current,
                        status: "done"
                    });
                    setIsSending(false);
                    isSendingRef.current = false;
                },
                onError: (data) => {
                    if (abortController.signal.aborted) return;
                    console.error("Rule agent chat turn error:", data);
                    updateAssistantMessage(assistantMessageId, { status: "error" });
                    setHasChatError(true);
                    setIsSending(false);
                    isSendingRef.current = false;
                }
            });
        };

        if (sessionIdRef.current) {
            runTurn(sessionIdRef.current);
        } else {
            openRuleAgentSession({ token, signal: abortController.signal })
                .then((sessionId) => {
                    if (abortController.signal.aborted) return;
                    sessionIdRef.current = sessionId;
                    return runTurn(sessionId);
                })
                .catch((error) => {
                    if (abortController.signal.aborted) return;
                    console.error("Rule agent session open error:", error);
                    updateAssistantMessage(assistantMessageId, { status: "error" });
                    setHasChatError(true);
                    setIsSending(false);
                    isSendingRef.current = false;
                });
        }
    };

    const handlePanelClose = () => {
        // The toggle lives inside the panel — leaving the panel open-less with
        // the mode stuck on would make it unreachable.
        if (selectionMode && onSelectionModeToggle) onSelectionModeToggle();
        setIsOpen(false);
    };

    const handleSend = () => sendText(inputValue);

    const handleInputKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const canSend = !isSending && (inputValue.trim() || pendingAttachments.length > 0);

    if (!isOpen) {
        return (
            <ChatDropArea
                onDropAttachments={handleDropAttachments}
                style={{
                    width: "48px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    paddingTop: "30px",
                    marginLeft: "8px",
                    flexShrink: 0
                }}
            >
                <Tooltip title={<p className="tooltipText">{t("Assistente de Regras")}</p>}>
                    <button
                        type="button"
                        aria-label={t("Assistente de Regras")}
                        className="qia-launcher-btn"
                        onClick={() => setIsOpen(true)}
                    >
                        <QiaAvatar size={30} className="qia-launcher-avatar" />
                    </button>
                </Tooltip>
            </ChatDropArea>
        );
    }

    return (
        <ChatDropArea
            onDropAttachments={handleDropAttachments}
            style={{
                width: panelWidth + "px",
                height: "100%",
                display: "flex",
                flexDirection: "row",
                marginLeft: "8px",
                flexShrink: 0
            }}
        >
            <div className="qia-resize-handle" onMouseDown={handleResizeMouseDown} />
            <div
                className="qia-panel"
                style={{
                    flexGrow: 1,
                    minWidth: 0,
                    border: "1px solid #E8EDF3",
                    borderRadius: "14px",
                    boxShadow: "0 2px 14px rgba(20,40,80,.06)"
                }}
            >
                <div className="qia-panel-header">
                    <div className="qia-header-left">
                        <QiaAvatar size={22} />
                        <span className="qia-panel-title">{t("Assistente de Regras")}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                        {onSelectionModeToggle && (
                            <Tooltip
                                title={
                                    <p className="tooltipText">
                                        {selectionMode ? t("Sair do modo de seleção") : t("Selecionar trechos para o chat")}
                                    </p>
                                }
                            >
                                <IconButton
                                    aria-label={selectionMode ? t("Sair do modo de seleção") : t("Selecionar trechos para o chat")}
                                    size="small"
                                    onClick={onSelectionModeToggle}
                                    className={selectionMode ? "qia-selection-toggle-active" : undefined}
                                    style={{ color: selectionMode ? "#2D9CFF" : "#717784" }}
                                >
                                    <TouchAppIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <IconButton
                            aria-label={t("Assistente de Regras")}
                            size="small"
                            onClick={handlePanelClose}
                            style={{ color: "#717784" }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </div>
                </div>
                <div ref={messageListRef} className="qia-body">
                    {messages.length === 0 ? (
                        <RuleChatWelcome
                            t={t}
                            onChipSend={sendText}
                            isEmptyTree={!treeData || treeData.length === 0}
                        />
                    ) : (
                        <div className="qia-messages">
                            {messages.map((message) => (
                                <RuleChatMessage
                                    key={message.id}
                                    message={message}
                                    t={t}
                                    rulesContext={rulesContext}
                                    userVariablesAvailable={userVariablesAvailable}
                                    onInsertGeneratedNodes={onInsertGeneratedNodes}
                                />
                            ))}
                        </div>
                    )}
                </div>
                {hasChatError && (
                    <div className="qia-error-banner">
                        {t("Não foi possível obter resposta do assistente. Tente novamente.")}
                    </div>
                )}
                <div className="qia-composer-wrap">
                    {pendingAttachments.length > 0 && (
                        <div className="qia-pending-attachments">
                            {pendingAttachments.map((attachment, index) => (
                                <ChatAttachmentCard
                                    key={index}
                                    attachment={attachment}
                                    onRemove={() => removePendingAttachment(index)}
                                    t={t}
                                />
                            ))}
                        </div>
                    )}
                    <div className="qia-composer-card">
                        <TextField
                            placeholder={
                                pendingAttachments.length > 0
                                    ? t("Pergunte algo sobre os trechos anexados...")
                                    : t("Digite sua mensagem...")
                            }
                            multiline
                            rowsMax={5}
                            fullWidth
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            value={inputValue}
                            onChange={(event) => setInputValue(event.target.value)}
                            onKeyDown={handleInputKeyDown}
                            disabled={isSending}
                            className="qia-composer-input"
                        />
                        <IconButton
                            aria-label={t("Enviar")}
                            onClick={handleSend}
                            disabled={!canSend}
                            size="small"
                            style={{ color: !canSend ? "#C8CDD6" : "#1E448A" }}
                        >
                            <SendIcon fontSize="small" />
                        </IconButton>
                    </div>
                </div>
            </div>
        </ChatDropArea>
    );
}

export default RuleChat;
